import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowRight,
  Check,
  ExternalLink,
  Layers,
  Link2,
  Pencil,
  Plus,
  Search,
  Sparkles,
  TriangleAlert,
  X,
} from 'lucide-react';
import {
  Button,
  Card,
  Field,
  Input,
  Pill,
  Select,
  Spinner,
  Textarea,
} from '../../../components/ui';
import { ConfidenceBadge } from '../../intelligence/components/Confidence';
import { organizationService } from '../../../services/organizationService';
import { previewNode, type MergePreview } from '../governmentApi';
import {
  GOV_NODE_KINDS,
  type ApplyResult,
  type ApplyUpdateInput,

  type GovDecision,
  type GovMatch,
  type GovNodeKind,
  type GovNodeView,
  type GovOfficeHolder,
  type GovUpdateView,
  type HeadChoice,
  type NodePatch,
  type NodePlan,
} from '../types';
import {
  KIND_ICON,
  KIND_LABEL,
  RELATION_LABEL,
  decisionSentence,
  endingSentence,
  govDate,
  hostOf,
  plural,
} from '../format';
import { EntityPlan, defaultHeadChoice, planIsNoop } from './EntityPlan';
import { OutcomeNote } from './ApplyOutcome';
import { EntityHistory, InPost } from './EntityActivity';
import { UpdateCard } from './UpdateCard';
import { SourceKindTag } from './SourceKindTag';

/** What each thing that moves is called, in the singular and the plural */
const MOVED_LABEL: Record<string, [string, string]> = {
  assignments: ['role', 'roles'],
  assignmentsDeduped: ['duplicate role dropped', 'duplicate roles dropped'],
  relationships: ['relationship', 'relationships'],
  influenceScores: ['influence score', 'influence scores'],
  media: ['media item', 'media items'],
  wikiPages: ['wiki page', 'wiki pages'],
  stances: ['position', 'positions'],
  watches: ['watch', 'watches'],
  signals: ['signal', 'signals'],
  monitoringAlerts: ['alert', 'alerts'],
  issueScenarios: ['scenario', 'scenarios'],
  ownership: ['owner', 'owners'],
  recordGrants: ['access grant', 'access grants'],
  changeProposals: ['change proposal', 'change proposals'],
  pendingEntries: ['pending entry', 'pending entries'],
  intelligenceLeads: ['AI lead', 'AI leads'],
  childOrganizations: ['sub-organization', 'sub-organizations'],
  organizationHistory: ['earlier version', 'earlier versions'],
  nameChanges: ['name change', 'name changes'],
  stakeholderNews: ['news feed', 'news feeds'],
};

const movedSentence = (moved: Record<string, number>): string => {
  const parts = Object.entries(moved)
    .filter(([, count]) => count > 0)
    .map(([key, count]) => {
      const [one, many] = MOVED_LABEL[key] ?? [key, key];
      return `${count} ${count === 1 ? one : many}`;
    });
  return parts.length ? parts.join(' · ') : 'nothing attached';
};

const SectionTitle: React.FC<{ children: React.ReactNode; aside?: React.ReactNode }> = ({
  children,
  aside,
}) => (
  <div className="mb-2 flex items-center justify-between gap-2">
    <h3 className="text-[12px] uppercase tracking-[0.06em] text-neutral-400">{children}</h3>
    {aside}
  </div>
);

/** One directory record that might be this entity */
const MatchRow: React.FC<{
  match: GovMatch;
  chosen: boolean;
  survivor: boolean;
  disabled?: boolean;
  onToggle: () => void;
  onKeep: () => void;
}> = ({ match, chosen, survivor, disabled, onToggle, onKeep }) => (
  <li className={`px-3 py-2.5 transition-colors ${chosen ? 'bg-white' : 'bg-neutral-50/60'}`}>
    <div className="flex items-start gap-2.5">
      <button
        type="button"
        role="checkbox"
        aria-checked={chosen}
        disabled={disabled}
        onClick={onToggle}
        className={`mt-0.5 flex h-4 w-4 flex-shrink-0 items-center justify-center rounded border transition-colors ${
          chosen ? 'border-primary-600 bg-primary-600 text-white' : 'border-neutral-300 bg-white'
        } ${disabled ? 'opacity-40' : ''}`}
      >
        {chosen && <Check className="h-3 w-3" strokeWidth={3} />}
      </button>
      <div className="min-w-0 flex-1">
        <div className="flex min-w-0 flex-wrap items-center gap-x-2 gap-y-1">
          <Link
            to={`/organizations/${match.organizationId}`}
            target="_blank"
            rel="noreferrer"
            className="truncate text-[13px] text-ink hover:underline hover:decoration-neutral-300 hover:underline-offset-2"
          >
            {match.label}
          </Link>
          {survivor && <Pill tone="teal">Kept</Pill>}
        </div>
        <p className="mt-0.5 truncate text-[12px] text-neutral-500">
          {[match.type, match.parentLabel ? `under ${match.parentLabel}` : null, plural(match.people, 'person', 'people')]
            .filter(Boolean)
            .join(' · ')}
        </p>
        <p className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-1 text-[11.5px] text-neutral-400">
          <span className="text-neutral-500">{RELATION_LABEL[match.relation]}</span>
          {match.reason && <span className="min-w-0 truncate">· {match.reason}</span>}
          <span>· {match.via === 'both' ? 'name and model agree' : match.via === 'ai' ? 'model' : match.via === 'manual' ? 'chosen by hand' : match.via === 'curated' ? 'found twice in the map' : 'name match'}</span>
          <span className="tabular-nums">· {match.score}</span>
        </p>
      </div>
      {chosen && !survivor && (
        <button
          type="button"
          onClick={onKeep}
          className="flex-shrink-0 rounded-full px-2 py-1 text-[11.5px] text-neutral-500 transition-colors hover:bg-neutral-100 hover:text-ink"
        >
          Keep this one
        </button>
      )}
    </div>
  </li>
);

/** Search the directory for a record the matching missed */
const AddMatch: React.FC<{ onAdd: (id: string, label: string) => void }> = ({ onAdd }) => {
  const [term, setTerm] = useState('');
  const [results, setResults] = useState<Array<{ id: string; name: string; type: string }>>([]);
  const [searching, setSearching] = useState(false);

  useEffect(() => {
    if (term.trim().length < 2) {
      setResults([]);
      return;
    }
    let cancelled = false;
    const timer = setTimeout(async () => {
      setSearching(true);
      try {
        const { data } = await organizationService.getOrganizations({ search: term.trim(), limit: 6 });
        if (!cancelled) setResults(data.map((org) => ({ id: org.id, name: org.name, type: org.type })));
      } catch {
        if (!cancelled) setResults([]);
      } finally {
        if (!cancelled) setSearching(false);
      }
    }, 250);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [term]);

  return (
    <div className="px-3 py-2.5">
      <div className="relative">
        <Search className="pointer-events-none absolute left-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-neutral-400" strokeWidth={1.75} />
        <Input
          value={term}
          onChange={(event) => setTerm(event.target.value)}
          placeholder="Find another record"
          className="h-8 pl-8 text-[12.5px]"
        />
        {searching && <Spinner className="absolute right-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2" />}
      </div>
      {results.length > 0 && (
        <ul className="mt-1.5 space-y-0.5">
          {results.map((result) => (
            <li key={result.id}>
              <button
                type="button"
                onClick={() => {
                  onAdd(result.id, result.name);
                  setTerm('');
                  setResults([]);
                }}
                className="flex w-full items-center gap-2 rounded-lg px-2 py-1.5 text-left text-[12.5px] transition-colors hover:bg-neutral-100"
              >
                <Plus className="h-3.5 w-3.5 flex-shrink-0 text-neutral-400" strokeWidth={2} />
                <span className="min-w-0 flex-1 truncate text-ink">{result.name}</span>
                <span className="flex-shrink-0 text-[11.5px] text-neutral-400">{result.type}</span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

/**
 * One entity of the structure, and the decision about it.
 *
 * Everything needed to judge it is here — where research found it, what it
 * says, which directory records look like it and what would happen to them —
 * because this is the last screen before records are merged and renamed.
 */
export const NodeInspector: React.FC<{
  node: GovNodeView;
  nodes: GovNodeView[];
  canEdit: boolean;
  busy?: boolean;
  onPatch: (patch: NodePatch) => Promise<unknown>;
  onStatus: (status: 'proposed' | 'confirmed' | 'rejected') => void;
  /** Applies the entity; with a head choice, records the head research found too */
  onApply: (head: HeadChoice | null) => void;
  onResearch: () => void;
  onCheckHolders: () => void;
  /** Research is already working or waiting, so this one will join the queue */
  queueing?: boolean;
  onClose?: () => void;
  /** Over the chart the panel floats, so it must be closable at any size */
  floating?: boolean;
  /** Who the directory records in post here */
  holders?: GovOfficeHolder[];
  /** Changes waiting for review here, and ones just reviewed on this page */
  waiting?: GovUpdateView[];
  /** What has been applied here */
  history?: GovUpdateView[];
  /** What applying would do, as the server works it out */
  plan?: NodePlan | null;
  planning?: boolean;
  planError?: string | null;
  onApplyUpdate?: (id: string, input: ApplyUpdateInput) => void;
  onDismissUpdate?: (id: string) => void;
  onVerifyUpdate?: (id: string) => void;
  onSelectNode?: (id: string) => void;
  /** It changed a moment ago, somewhere */
  changed?: boolean;
  /** What the last apply from this panel did */
  outcome?: ApplyResult | null;
  onCloseOutcome?: () => void;
}> = ({
  node,
  nodes,
  canEdit,
  busy,
  onPatch,
  onStatus,
  onApply,
  onResearch,
  onCheckHolders,
  queueing = false,
  onClose,
  floating,
  holders = [],
  waiting = [],
  history = [],
  plan = null,
  planning = false,
  planError = null,
  onApplyUpdate,
  onDismissUpdate,
  onVerifyUpdate,
  onSelectNode,
  changed = false,
  outcome = null,
  onCloseOutcome,
}) => {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState<NodePatch>({});
  const [preview, setPreview] = useState<MergePreview | null>(null);
  const [previewing, setPreviewing] = useState(false);
  const [extra, setExtra] = useState<GovMatch[]>([]);
  const [headChoice, setHeadChoice] = useState<HeadChoice | null>(null);

  useEffect(() => {
    setEditing(false);
    setDraft({});
    setPreview(null);
    setExtra([]);
  }, [node.id]);

  // The choice about the head starts from the plan, and starts again only
  // when the plan says something different about the head — not every time
  // the page reloads underneath it
  const headKey = plan?.head
    ? [
        plan.nodeId,
        plan.head.name,
        plan.head.action,
        plan.head.person.id ?? '',
        plan.head.closes.map((role) => role.assignmentId).join(','),
      ].join('|')
    : `${plan?.nodeId ?? node.id}|none`;
  useEffect(() => {
    setHeadChoice(defaultHeadChoice(plan?.head));
    // headKey stands for the parts of the plan that matter here
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [headKey]);

  const Icon = KIND_ICON[node.kind];
  const matches = useMemo(() => [...node.matches, ...extra], [node.matches, extra]);
  const decision = node.decision;
  const chosen = useMemo(() => {
    const ids = new Set<string>();
    if (decision.survivorId) ids.add(decision.survivorId);
    for (const id of decision.mergeIds ?? []) ids.add(id);
    return ids;
  }, [decision]);

  const setDecision = (next: GovDecision) => onPatch({ decision: next });

  const toggleMatch = (id: string) => {
    const next = new Set(chosen);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    const ids = Array.from(next);
    if (!ids.length) {
      void setDecision({ action: 'create', survivorId: null, mergeIds: [] });
      return;
    }
    const survivorId =
      node.organizationId ??
      (decision.survivorId && next.has(decision.survivorId)
        ? decision.survivorId
        : ids.sort(
            (a, b) =>
              (matches.find((m) => m.organizationId === b)?.people ?? 0) -
              (matches.find((m) => m.organizationId === a)?.people ?? 0),
          )[0]);
    void setDecision({
      action: ids.length > 1 ? 'merge' : 'link',
      survivorId,
      mergeIds: ids.filter((id) => id !== survivorId),
    });
  };

  const keepThisOne = (id: string) => {
    const ids = Array.from(chosen);
    void setDecision({
      action: ids.length > 1 ? 'merge' : 'link',
      survivorId: id,
      mergeIds: ids.filter((other) => other !== id),
    });
  };

  const addMatch = (id: string, label: string) => {
    if (matches.some((match) => match.organizationId === id)) return;
    setExtra((current) => [
      ...current,
      {
        organizationId: id,
        label,
        type: null,
        parentLabel: null,
        people: 0,
        score: 100,
        relation: 'same',
        reason: 'Chosen by hand',
        via: 'manual',
      },
    ]);
    const ids = [...Array.from(chosen), id];
    void setDecision({
      action: ids.length > 1 ? 'merge' : 'link',
      survivorId: node.organizationId ?? decision.survivorId ?? id,
      mergeIds: ids.filter((other) => other !== (node.organizationId ?? decision.survivorId ?? id)),
    });
  };

  const showPreview = async () => {
    setPreviewing(true);
    try {
      setPreview(await previewNode(node.id));
    } catch {
      setPreview({ survivor: null, merges: [] });
    } finally {
      setPreviewing(false);
    }
  };

  const save = async () => {
    await onPatch(draft);
    setEditing(false);
    setDraft({});
  };

  const parentName = node.parentId ? nodes.find((item) => item.id === node.parentId)?.name : null;
  // An applied entity whose record already says everything the map does
  const upToDate = node.status === 'applied' && planIsNoop(plan);

  return (
    // min-h-0 flex-1: in the phone sheet (a capped height, not a set one) h-full does nothing, and the
    // inspector grew past the sheet with its body cut off; as a flex item it fills the sheet and scrolls
    <div className="flex h-full min-h-0 flex-1 flex-col">
      {/* ---- header ---- */}
      <div className="flex items-start gap-3 border-b border-neutral-200/70 px-4 py-3.5">
        <span className="mt-0.5 inline-flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-xl bg-neutral-100 text-neutral-500">
          <Icon className="h-4 w-4" strokeWidth={1.75} />
        </span>
        <div className="min-w-0 flex-1">
          <h2 className="break-words text-[15px] leading-snug text-ink">{node.name}</h2>
          <p className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-1 text-[12px] text-neutral-500">
            <span>{KIND_LABEL[node.kind]}</span>
            {parentName && (
              <>
                <span className="text-neutral-300">›</span>
                <span className="truncate">under {parentName}</span>
              </>
            )}
          </p>
          <div className="mt-2 flex flex-wrap items-center gap-1.5">
            <ConfidenceBadge value={node.confidence} />
            {node.status === 'applied' && <Pill tone="green">In the directory</Pill>}
            {node.status === 'confirmed' && <Pill tone="neutral">Confirmed</Pill>}
            {node.status === 'proposed' && <Pill tone="amber">Not reviewed</Pill>}
            {node.status === 'rejected' && <Pill tone="red">Rejected</Pill>}
            {node.stale && !node.ended && <Pill tone="amber">Not found again</Pill>}
            {node.ended && <Pill tone="violet">Ended</Pill>}
            {waiting.some((update) => update.status === 'pending') && (
              <Pill tone="violet">
                {plural(waiting.filter((update) => update.status === 'pending').length, 'change')} waiting
              </Pill>
            )}
            {node.edited && <Pill tone="outline">Edited</Pill>}
            {changed && <Pill tone="amber">Just updated</Pill>}
          </div>
        </div>
        <div className="flex flex-shrink-0 items-center gap-1">
          {canEdit && !editing && (
            <button
              type="button"
              onClick={() => {
                setEditing(true);
                setDraft({
                  name: node.name,
                  nameEn: node.nameEn,
                  acronym: node.acronym,
                  kind: node.kind,
                  parentId: node.parentId,
                  website: node.website,
                  description: node.description,
                });
              }}
              aria-label="Edit this entity"
              className="inline-flex h-7 w-7 items-center justify-center rounded-full text-neutral-400 transition-colors hover:bg-neutral-100 hover:text-ink"
            >
              <Pencil className="h-3.5 w-3.5" strokeWidth={1.75} />
            </button>
          )}
          {onClose && (
            <button
              type="button"
              onClick={onClose}
              aria-label="Close"
              className={`inline-flex h-7 w-7 items-center justify-center rounded-full text-neutral-400 transition-colors hover:bg-neutral-100 hover:text-ink ${
                floating ? '' : 'lg:hidden'
              }`}
            >
              <X className="h-4 w-4" strokeWidth={1.75} />
            </button>
          )}
        </div>
      </div>

      <div className="min-h-0 flex-1 space-y-5 overflow-y-auto px-4 py-4">
        {/* ---- it no longer exists ---- */}
        {node.ended && (
          <div className="rounded-xl bg-violet-50/70 px-3 py-2.5 text-[12.5px] text-violet-900">
            <p>{endingSentence(node.ended)}.</p>
            <p className="mt-1 text-[12px] text-violet-800/80">
              Its record stays in the directory with its history.
            </p>
            {canEdit && (
              <button
                type="button"
                onClick={() => onPatch({ ended: null })}
                className="mt-2 text-[12px] text-violet-900 underline decoration-violet-300 underline-offset-2 hover:decoration-violet-600"
              >
                It still exists
              </button>
            )}
            {node.ended.into.some((ref) => ref.nodeId) && onSelectNode && (
              <div className="mt-2 flex flex-wrap gap-1.5">
                {node.ended.into
                  .filter((ref) => ref.nodeId && nodes.some((item) => item.id === ref.nodeId))
                  .map((ref) => (
                    <button
                      key={ref.nodeId}
                      type="button"
                      onClick={() => onSelectNode(ref.nodeId!)}
                      className="inline-flex items-center gap-1 rounded-full bg-white px-2.5 py-1 text-[12px] text-ink ring-1 ring-violet-200 transition-colors hover:bg-violet-50"
                    >
                      {ref.label}
                      <ArrowRight className="h-3 w-3" strokeWidth={1.75} />
                    </button>
                  ))}
              </div>
            )}
          </div>
        )}

        {/* ---- editing ---- */}
        {editing && (
          <div className="space-y-3 rounded-xl border border-neutral-200 p-3">
            <Field label="Official name" htmlFor="node-name">
              <Input
                id="node-name"
                value={draft.name ?? ''}
                onChange={(event) => setDraft({ ...draft, name: event.target.value })}
              />
            </Field>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <Field label="English name" htmlFor="node-name-en">
                <Input
                  id="node-name-en"
                  value={draft.nameEn ?? ''}
                  onChange={(event) => setDraft({ ...draft, nameEn: event.target.value })}
                />
              </Field>
              <Field label="Acronym" htmlFor="node-acronym">
                <Input
                  id="node-acronym"
                  value={draft.acronym ?? ''}
                  onChange={(event) => setDraft({ ...draft, acronym: event.target.value })}
                />
              </Field>
            </div>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <Field label="Kind" htmlFor="node-kind">
                <Select
                  id="node-kind"
                  value={draft.kind ?? node.kind}
                  onChange={(event) => setDraft({ ...draft, kind: event.target.value as GovNodeKind })}
                >
                  {GOV_NODE_KINDS.map((kind) => (
                    <option key={kind} value={kind}>
                      {KIND_LABEL[kind]}
                    </option>
                  ))}
                </Select>
              </Field>
              <Field label="Sits under" htmlFor="node-parent">
                <Select
                  id="node-parent"
                  value={draft.parentId ?? ''}
                  onChange={(event) => setDraft({ ...draft, parentId: event.target.value || null })}
                >
                  <option value="">Top level</option>
                  {nodes
                    .filter((item) => item.id !== node.id && item.status !== 'rejected')
                    .map((item) => (
                      <option key={item.id} value={item.id}>
                        {item.name}
                      </option>
                    ))}
                </Select>
              </Field>
            </div>
            <Field label="Website" htmlFor="node-website">
              <Input
                id="node-website"
                value={draft.website ?? ''}
                onChange={(event) => setDraft({ ...draft, website: event.target.value })}
              />
            </Field>
            <Field label="What it does" htmlFor="node-description">
              <Textarea
                id="node-description"
                rows={2}
                value={draft.description ?? ''}
                onChange={(event) => setDraft({ ...draft, description: event.target.value })}
              />
            </Field>
            <div className="flex justify-end gap-2">
              <Button size="sm" variant="secondary" onClick={() => setEditing(false)}>
                Cancel
              </Button>
              <Button size="sm" variant="primary" onClick={save} loading={busy}>
                Save
              </Button>
            </div>
          </div>
        )}

        {!editing && (node.nameEn || node.description || node.website) && (
          <div className="space-y-1.5 text-[13px]">
            {node.nameEn && node.nameEn !== node.name && (
              <p className="text-neutral-500">{node.nameEn}</p>
            )}
            {node.description && <p className="text-neutral-700">{node.description}</p>}
            {node.website && (
              <a
                href={node.website}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1 text-[12.5px] text-neutral-500 underline decoration-neutral-300 underline-offset-2 hover:text-ink"
              >
                {hostOf(node.website)}
                <ExternalLink className="h-3 w-3" strokeWidth={1.75} />
              </a>
            )}
          </div>
        )}

        {/* ---- who holds its posts ---- */}
        {(node.organizationId || node.head?.name) && (
          <section>
            <SectionTitle
              aside={
                node.status === 'applied' &&
                canEdit && (
                  <button
                    type="button"
                    onClick={onCheckHolders}
                    className="inline-flex items-center gap-1 text-[12px] text-neutral-500 underline decoration-neutral-300 underline-offset-2 hover:text-ink"
                  >
                    <Sparkles className="h-3 w-3" strokeWidth={1.75} />
                    {queueing ? 'Queue a check' : 'Check again'}
                  </button>
                )
              }
            >
              In post
            </SectionTitle>
            <InPost node={node} holders={holders} />
          </section>
        )}

        {/* ---- what research says has changed here ---- */}
        {waiting.length > 0 && (
          <section>
            <SectionTitle>Waiting for review</SectionTitle>
            <div className="space-y-2">
              {waiting.map((update) => (
                <UpdateCard
                  key={update.id}
                  update={update}
                  canEdit={canEdit}
                  busy={busy}
                  compact
                  onApply={(input) => onApplyUpdate?.(update.id, input)}
                  onDismiss={() => onDismissUpdate?.(update.id)}
                  onVerify={onVerifyUpdate ? () => onVerifyUpdate(update.id) : undefined}
                />
              ))}
            </div>
          </section>
        )}

        {/* ---- the decision ---- */}
        <section>
          <SectionTitle
            aside={
              matches.length > 0 &&
              (decision.mergeIds?.length ?? 0) > 0 && (
                <button
                  type="button"
                  onClick={showPreview}
                  disabled={previewing}
                  className="text-[12px] text-neutral-500 underline decoration-neutral-300 underline-offset-2 hover:text-ink"
                >
                  {previewing ? 'Checking…' : 'What would move?'}
                </button>
              )
            }
          >
            In your directory
          </SectionTitle>

          <div className="overflow-hidden rounded-xl border border-neutral-200">
            <p className="flex items-start gap-2 border-b border-neutral-200/70 bg-neutral-50/70 px-3 py-2.5 text-[12.5px] text-neutral-600">
              {decision.action === 'merge' ? (
                <Layers className="mt-px h-3.5 w-3.5 flex-shrink-0 text-teal-600" strokeWidth={1.75} />
              ) : decision.action === 'create' ? (
                <Plus className="mt-px h-3.5 w-3.5 flex-shrink-0 text-neutral-400" strokeWidth={2} />
              ) : (
                <Link2 className="mt-px h-3.5 w-3.5 flex-shrink-0 text-neutral-400" strokeWidth={1.75} />
              )}
              <span className="min-w-0">{decisionSentence(node)}</span>
            </p>

            {node.organization && (
              <div className="flex items-center gap-2 border-b border-neutral-200/70 px-3 py-2.5">
                <Check className="h-3.5 w-3.5 flex-shrink-0 text-emerald-600" strokeWidth={2} />
                <Link
                  to={`/organizations/${node.organization.id}`}
                  target="_blank"
                  rel="noreferrer"
                  className="min-w-0 flex-1 truncate text-[13px] text-ink hover:underline hover:decoration-neutral-300 hover:underline-offset-2"
                >
                  {node.organization.label}
                </Link>
                <span className="flex-shrink-0 text-[12px] text-neutral-400">
                  {plural(node.organization.people, 'person', 'people')}
                </span>
              </div>
            )}

            {matches.length > 0 ? (
              <ul className="divide-y divide-neutral-100">
                {matches.map((match) => (
                  <MatchRow
                    key={match.organizationId}
                    match={match}
                    chosen={chosen.has(match.organizationId)}
                    survivor={decision.survivorId === match.organizationId}
                    disabled={!canEdit}
                    onToggle={() => canEdit && toggleMatch(match.organizationId)}
                    onKeep={() => canEdit && keepThisOne(match.organizationId)}
                  />
                ))}
              </ul>
            ) : (
              <p className="px-3 py-3 text-[12.5px] text-neutral-400">
                No record in the directory looks like this entity.
              </p>
            )}

            {canEdit && <AddMatch onAdd={addMatch} />}
          </div>

          {preview && (
            <div className="mt-2 space-y-1.5 rounded-xl bg-neutral-50 px-3 py-2.5 text-[12.5px]">
              {preview.merges.length === 0 ? (
                <p className="text-neutral-500">Nothing would be folded together.</p>
              ) : (
                preview.merges.map((merge) => (
                  <div key={merge.id}>
                    <p className="flex flex-wrap items-center gap-1.5 text-neutral-700">
                      <span className="truncate">{merge.label}</span>
                      <ArrowRight className="h-3 w-3 flex-shrink-0 text-neutral-400" strokeWidth={1.75} />
                      <span className="truncate">{preview.survivor?.label}</span>
                    </p>
                    <p className="text-neutral-500">Moves {movedSentence(merge.moved)}</p>
                    {merge.warnings.map((warning) => (
                      <p key={warning} className="mt-0.5 flex items-start gap-1.5 text-amber-700">
                        <TriangleAlert className="mt-px h-3 w-3 flex-shrink-0" strokeWidth={1.75} />
                        <span>{warning}</span>
                      </p>
                    ))}
                  </div>
                ))
              )}
            </div>
          )}

          {canEdit && node.status !== 'applied' && (
            <div className="mt-2 flex flex-wrap gap-2">
              <Button
                size="xs"
                variant={decision.action === 'create' ? 'dark' : 'secondary'}
                onClick={() => setDecision({ action: 'create', survivorId: null, mergeIds: [] })}
              >
                Create a new record
              </Button>
              <Button
                size="xs"
                variant={decision.action === 'skip' ? 'dark' : 'secondary'}
                onClick={() => setDecision({ action: 'skip', survivorId: null, mergeIds: [] })}
              >
                Leave out
              </Button>
            </div>
          )}
        </section>

        {/* ---- what applying does ---- */}
        {canEdit && node.status !== 'rejected' && (
          <section>
            <SectionTitle>
              {node.status === 'applied' ? 'What “Update record” does' : 'What applying does'}
            </SectionTitle>
            <EntityPlan
              plan={plan}
              loading={planning}
              error={planError}
              applied={node.status === 'applied'}
              headChoice={headChoice}
              canEdit={canEdit}
              onHeadChoice={setHeadChoice}
            />
          </section>
        )}

        {/* ---- what has been done here ---- */}
        {node.organizationId && (
          <section>
            <SectionTitle>History</SectionTitle>
            <EntityHistory history={history} />
          </section>
        )}

        {/* ---- the other ways research has written it ---- */}
        {node.variants.length > 0 && (
          <section>
            <SectionTitle>Also written</SectionTitle>
            <ul className="space-y-1 text-[12.5px] text-neutral-600">
              {node.variants.map((variant) => (
                <li key={variant} className="break-words">
                  {variant}
                </li>
              ))}
            </ul>
            <p className="mt-1.5 text-[11.5px] text-neutral-400">
              The same body, as research has written it. Found again under any of these, it lands here.
            </p>
          </section>
        )}

        {/* ---- former names ---- */}
        {node.formerNames.length > 0 && (
          <section>
            <SectionTitle>Formerly</SectionTitle>
            <ul className="space-y-1 text-[12.5px]">
              {node.formerNames.map((former) => (
                <li key={former.name} className="flex flex-wrap items-baseline gap-x-2 text-neutral-600">
                  <span className="min-w-0 break-words">{former.name}</span>
                  {former.until && <span className="text-neutral-400">until {govDate(former.until)}</span>}
                </li>
              ))}
            </ul>
          </section>
        )}

        {/* ---- sources ---- */}
        <section>
          <SectionTitle>Sources</SectionTitle>
          {node.sources.length === 0 ? (
            <p className="text-[12.5px] text-neutral-400">
              Nothing cited — this entity is only as good as a second look at it.
            </p>
          ) : (
            <ol className="space-y-2">
              {node.sources.map((source, index) => (
                <li key={source.url} className="flex gap-2 text-[12.5px] leading-snug">
                  <span className="mt-px inline-flex h-4 w-4 flex-shrink-0 items-center justify-center rounded bg-neutral-100 text-[10px] tabular-nums text-neutral-500">
                    {index + 1}
                  </span>
                  <span className="min-w-0">
                    {source.claim && <span className="text-neutral-700">{source.claim}</span>}
                    <span className="mt-0.5 flex flex-wrap items-center gap-x-1.5 text-[11.5px] text-neutral-400">
                      <a
                        href={source.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex min-w-0 items-center gap-1 truncate text-neutral-500 underline decoration-neutral-300 underline-offset-2 hover:text-ink"
                      >
                        {source.publisher || hostOf(source.url)}
                        <ExternalLink className="h-3 w-3 flex-shrink-0" strokeWidth={1.75} />
                      </a>
                      {source.date && <span>· {govDate(source.date)}</span>}
                    <SourceKindTag kind={source.kind} />
                      {source.status === 'dead' && <span className="text-primary-600">· page gone</span>}
                    </span>
                  </span>
                </li>
              ))}
            </ol>
          )}
        </section>
      </div>

      {/* ---- what the last apply did, where it was pressed ---- */}
      {outcome && onCloseOutcome && (
        <OutcomeNote result={outcome} name={node.name} onClose={onCloseOutcome} />
      )}

      {/* ---- actions ---- */}
      {canEdit && (
        <div
          className="flex flex-wrap items-center gap-2 border-t border-neutral-200/70 px-4 py-3"
          style={{ paddingBottom: 'max(0.75rem, env(safe-area-inset-bottom))' }}
        >
          {node.status === 'proposed' && (
            <Button size="sm" variant="secondary" onClick={() => onStatus('confirmed')} disabled={busy}>
              <Check className="h-3.5 w-3.5" strokeWidth={2} />
              Confirm
            </Button>
          )}
          {node.status !== 'applied' && node.status !== 'rejected' && (
            <Button size="sm" variant="ghost" onClick={() => onStatus('rejected')} disabled={busy}>
              Not part of the state
            </Button>
          )}
          {node.status === 'rejected' && (
            <Button size="sm" variant="secondary" onClick={() => onStatus('proposed')} disabled={busy}>
              Put back
            </Button>
          )}
          <Button
            size="sm"
            variant="ghost"
            onClick={onResearch}
            disabled={busy}
            title={queueing ? 'Research is running; this will join the queue' : undefined}
          >
            <Sparkles className="h-3.5 w-3.5" strokeWidth={1.75} />
            {queueing ? 'Queue research inside' : 'Research inside'}
          </Button>
          <span className="flex-1" />
          {node.status !== 'rejected' &&
            decision.action !== 'skip' &&
            (upToDate ? (
              // Nothing left to write: saying so beats a button that does nothing
              <Button size="sm" variant="secondary" disabled title="The record already matches the map">
                <Check className="h-3.5 w-3.5 text-emerald-600" strokeWidth={2} />
                Up to date
              </Button>
            ) : (
              <Button
                size="sm"
                variant="primary"
                onClick={() => onApply(headChoice)}
                disabled={busy || planning || !!plan?.blocked}
                loading={busy}
                title={plan?.blocked ?? undefined}
              >
                {node.status === 'applied'
                  ? decision.action === 'merge'
                    ? 'Fold duplicates in'
                    : 'Update record'
                  : decision.action === 'create'
                    ? 'Create record'
                    : decision.action === 'merge'
                      ? 'Merge and apply'
                      : 'Apply'}
              </Button>
            ))}
        </div>
      )}
    </div>
  );
};

export const InspectorEmpty: React.FC = () => (
  <Card className="hidden h-full items-center justify-center p-8 text-center lg:flex">
    <div>
      <Sparkles className="mx-auto h-5 w-5 text-neutral-300" strokeWidth={1.5} />
      <p className="mt-2 text-[13px] text-neutral-500">Pick an entity to review it</p>
      <p className="mt-1 text-[12px] text-neutral-400">
        Its sources, the records it matches and what applying would do
      </p>
    </div>
  </Card>
);
